import { motion } from "framer-motion";
import { Heart } from "lucide-react";

interface LoveLetterProps {
  name?: string;
}

const LoveLetter = ({ name }: LoveLetterProps) => {
  const poems = [
    {
      title: "A Valentine's Promise",
      lines: [
        `${name ? `Dear ${name},` : "My Dearest,"}`,
        "",
        "Like stars that light the midnight sky,",
        "My love for you will never die.",
        "With every beat of my heart so true,",
        "I promise to always cherish you.",
        "",
        "Through sunny days and stormy weather,",
        "We'll face this world, forever together.",
        "So take my hand, let's start anew,",
        "For all I want is to be with you. 💕",
      ],
    },
    {
      title: "My Heart Belongs to You",
      lines: [
        `${name ? `To ${name},` : "To My Love,"}`,
        "",
        "Roses are red, violets are blue,",
        "But nothing compares to my love for you.",
        "You make my heart skip and my soul sing,",
        "You are my everything. 💖",
        "",
        "Forever yours,",
        "Your Valentine ❤️",
      ],
    },
    {
      title: "Written in the Stars",
      lines: [
        `${name ? `My beloved ${name},` : "My Beloved,"}`,
        "",
        "In a universe of endless wonder,",
        "You're the lightning to my thunder.",
        "Every moment we're apart,",
        "You're always in my heart. 💝",
        "",
        "With all my love,",
        "Yours forever 💘",
      ],
    },
    {
      title: "You Complete Me",
      lines: [
        `${name ? `Dearest ${name},` : "My Dear One,"}`,
        "",
        "Before you, my days were gray,",
        "Now sunshine follows me each day.",
        "Your smile lights up my world so bright,",
        "You make everything feel right. ✨",
        "",
        "With endless love,",
        "Your Valentine 💗",
      ],
    },
  ];

  const poem = poems[Math.floor(Math.random() * poems.length)];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, rotateY: -90 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
      className="relative"
    >
      {/* Envelope Opening Animation */}
      <motion.div
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="absolute inset-0 bg-gradient-to-br from-pink-600 to-red-600 rounded-2xl origin-top z-10"
      />

      {/* Letter Content */}
      <div className="p-8 rounded-2xl bg-gradient-to-br from-pink-500/10 to-red-500/10 border border-pink-500/30 backdrop-blur-md max-w-md mx-auto shadow-[0_0_30px_rgba(236,72,153,0.3)]">
        {/* Decorative corners with glow */}
        <motion.div 
          className="absolute top-3 left-3"
          animate={{ 
            filter: ["drop-shadow(0 0 4px rgba(236,72,153,0.5))", "drop-shadow(0 0 8px rgba(236,72,153,0.8))", "drop-shadow(0 0 4px rgba(236,72,153,0.5))"]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
        </motion.div>
        <motion.div 
          className="absolute top-3 right-3"
          animate={{ 
            filter: ["drop-shadow(0 0 4px rgba(236,72,153,0.5))", "drop-shadow(0 0 8px rgba(236,72,153,0.8))", "drop-shadow(0 0 4px rgba(236,72,153,0.5))"]
          }}
          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
        >
          <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
        </motion.div>
        <motion.div 
          className="absolute bottom-3 left-3"
          animate={{ 
            filter: ["drop-shadow(0 0 4px rgba(236,72,153,0.5))", "drop-shadow(0 0 8px rgba(236,72,153,0.8))", "drop-shadow(0 0 4px rgba(236,72,153,0.5))"]
          }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        >
          <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
        </motion.div>
        <motion.div 
          className="absolute bottom-3 right-3"
          animate={{ 
            filter: ["drop-shadow(0 0 4px rgba(236,72,153,0.5))", "drop-shadow(0 0 8px rgba(236,72,153,0.8))", "drop-shadow(0 0 4px rgba(236,72,153,0.5))"]
          }}
          transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
        >
          <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
        </motion.div>

        {/* Letter content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <h3 className="text-xl font-semibold text-pink-300 mb-4 text-center">
            💌 {poem.title}
          </h3>
          <div className="space-y-1 text-center">
            {poem.lines.map((line, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className={`text-pink-100 ${
                  line === "" ? "h-4" : ""
                } ${
                  line.startsWith("Dear") || line.startsWith("To ") || line.startsWith("My beloved") || line.startsWith("Dearest") || line.startsWith("Forever") || line.startsWith("Your") || line.startsWith("With")
                    ? "italic text-pink-300"
                    : ""
                }`}
              >
                {line}
              </motion.p>
            ))}
          </div>
        </motion.div>

        {/* Wax seal effect with animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 1.5, type: "spring", damping: 10 }}
          className="absolute -bottom-5 left-1/2 -translate-x-1/2"
        >
          <motion.div 
            className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg"
            animate={{
              boxShadow: [
                "0 4px 6px rgba(0,0,0,0.1), 0 0 15px rgba(239,68,68,0.3)",
                "0 4px 6px rgba(0,0,0,0.1), 0 0 25px rgba(239,68,68,0.5)",
                "0 4px 6px rgba(0,0,0,0.1), 0 0 15px rgba(239,68,68,0.3)",
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Heart className="w-7 h-7 text-white fill-white" />
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoveLetter;
